import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactPaginate from "react-paginate";
import api from "../../axiosConfig";
import {
  Table,
  Button,
  InputGroup,
  FormControl,
  Container,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../pagination.css";

export default function ContCcMapList() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const searchRef = useRef(null);
  const fetchItems = useCallback(async () => {
    const skip = currentPage * itemsPerPage;

    try {
      setLoading(true);
      setErr("");

      const res = await api.get("/cont_cc_map", {
        params: { q: q || undefined, skip, limit: itemsPerPage },
      });

      const data = res.data;
      const rows = Array.isArray(data) ? data : (data.items ?? []);
      const total = Array.isArray(data) ? rows.length : (data.total ?? 0);

      setItems(rows);
      setTotalCount(total);
    } catch (e) {
      console.error("Eroare la încărcare cont_cc_map:", e);
      setErr(e?.response?.data?.detail || e?.message || "Eroare la încărcare.");
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [q, currentPage, itemsPerPage]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearchChange = (e) => {
    setSearchParams({ q: e.target.value });
    setCurrentPage(0);
  };

  const handleClear = () => {
    setSearchParams({});
    setCurrentPage(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Sigur dorești să ștergi maparea #${id}?`)) return;

    try {
      await api.delete(`/cont_cc_map/${id}`);
      fetchItems();
    } catch (e) {
      alert(
        "Eroare la ștergere: " +
          (e?.response?.data?.detail || e?.message || "unknown"),
      );
    }
  };

  const pageCount = Math.max(1, Math.ceil((totalCount || 0) / itemsPerPage));

  const nice = (s) => {
    const t = String(s ?? "").trim();
    if (!t) return "";
    // dacă vine "None • administrativ", curățăm pentru display
    return t.startsWith("None • ") ? t.replace(/^None •\s*/, "") : t;
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="mb-0">Mapări cont → centru de cost</h2>
        <div className="d-flex align-items-center gap-2">
          {loading ? <Spinner size="sm" /> : null}
          <Button onClick={() => navigate("/cont-cc-map/create")}>
            Adaugă
          </Button>
        </div>
      </div>

      <InputGroup className="my-3">
        <FormControl
          ref={searchRef}
          placeholder="Caută (cont / centru de cost)…"
          value={q}
          onChange={handleSearchChange}
        />
        {q ? (
          <Button variant="outline-danger" onClick={handleClear}>
            Curăță
          </Button>
        ) : null}
      </InputGroup>

      <div className="mb-3">
        <label>
          Rânduri pe pagină:&nbsp;
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(0);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </label>
      </div>

      {err ? (
        <Alert variant="danger" className="mb-3">
          {err}
        </Alert>
      ) : null}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th style={{ width: 80 }}>ID</th>
            <th>Cont</th>
            <th>Centru de cost</th>
            <th style={{ width: 140 }} className="text-end">
              Acțiuni
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length ? (
            items.map((it) => (
              <tr key={it.id}>
                <td>{it.id}</td>

                <td>
                  <div style={{ fontWeight: 600 }}>
                    {nice(it.cont_label) || `Cont #${it.cont_id}`}
                  </div>
                  <div className="small text-muted">cont_id={it.cont_id}</div>
                </td>

                <td>
                  <div style={{ fontWeight: 600 }}>
                    {nice(it.cc_label) ||
                      nice(it.cost_center_label) ||
                      `CC #${it.cost_center_id}`}
                  </div>
                  <div className="small text-muted">
                    cost_center_id={it.cost_center_id}
                  </div>
                </td>

                <td className="text-end">
                  {/* optional: edit route dacă o ai */}
                  {/* <Button size="sm" className="me-2" onClick={() => navigate(`/cont-cc-map/${it.id}`)}>Editează</Button> */}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(it.id)}
                  >
                    Șterge
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="text-center text-muted">
                Nu există mapări.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <ReactPaginate
        previousLabel={"← Înapoi"}
        nextLabel={"Înainte →"}
        pageCount={pageCount}
        forcePage={currentPage}
        onPageChange={(event) => setCurrentPage(event.selected)}
        containerClassName={"pagination justify-content-center"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </Container>
  );
}
